import {
  Box,
  type BoxProps,
  HStack,
  type StackProps,
  Text,
  type TextProps,
  VStack,
} from "@chakra-ui/react";
import { Instant, LocalDateTime, nativeJs } from "@js-joda/core";
import React from "react";
import { auth } from "../../../../core/auth";
import { env } from "../../../../env";
import PatientTaskChat from "../../../../shared/components/PatientTaskChat";
import type { PatientId } from "../../../../shared/schema/schema";
import { EntityProfileEmptyState } from "../../../entityProfileModal/components/EntityProfileFeedback";
import EntityProfileQuerySection from "../../../entityProfileModal/components/EntityProfileQuerySection";
import EntityProfileRecentActivityTimeline, {
  type EntityProfileRecentActivityTimelineItem,
} from "../../../entityProfileModal/components/EntityProfileRecentActivityTimeline";
import { entityProfileModalColors } from "../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../../patientProfileModal.actions";
import { formatPatientProfileValue } from "../../patientProfileModal.utils";
import {
  usePatientRecentActivityActorPhotos,
  usePatientRecentActivity,
  type PatientRecentActivityChange,
  type PatientRecentActivityRow,
} from "./PatientProfileRecentActivitySection.hooks";
import EntityModalButton from "../../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  patientId: PatientId;
};

const activityRootProps: StackProps = {
  alignItems: "stretch",
  spacing: 4,
};

const activityOnboardingProps: StackProps = {
  alignItems: "stretch",
  spacing: 3,
};

const activityOperationTextProps: TextProps = {
  as: "span",
  color: "#808080",
};

const activityChangeTextBaseProps: TextProps = {
  color: entityProfileModalColors.text,
  fontSize: "14px",
  lineHeight: "26px",
};

const activityChangeFieldProps: BoxProps = {
  as: "span",
  borderBottom: "1px dashed #111827",
};

const activityMultiChangeListProps: StackProps = {
  alignItems: "stretch",
  mt: 0,
  spacing: 0,
};

function getActivityChangeTextProps(isInline: boolean | undefined): TextProps {
  return {
    ...activityChangeTextBaseProps,
    as: isInline ? "span" : "div",
    display: isInline ? "inline" : "block",
  };
}

function formatActivityDate(value: unknown) {
  if (value instanceof LocalDateTime || value instanceof Instant) {
    return formatPatientProfileValue(value);
  }

  if (typeof value === "string") {
    const date = new Date(value);

    if (Number.isNaN(date.getTime()) === false) {
      return formatPatientProfileValue(LocalDateTime.from(nativeJs(date)));
    }
  }

  return value === null || value === undefined ? "" : String(value);
}

function formatTableName(tableName: string | null | undefined, actionStr: string) {
  if (tableName === null || tableName === undefined) {
    return "";
  }

  if (tableName.endsWith("_assoc") && ["ASSOCIATE", "DISASSOCIATE"].includes(actionStr)) {
    return "";
  }

  return tableName.replace(/_/g, " ");
}

function getArticle(value: string | null | undefined) {
  const firstLetter = value?.trim().charAt(0).toLowerCase();

  return firstLetter !== undefined && ["a", "e", "i", "o"].includes(firstLetter) ? "an" : "a";
}

function getOperation(activity: PatientRecentActivityRow) {
  const firstChange = activity.changes[0];
  const hasUpdatedValue = firstChange?.old !== null && firstChange?.old !== undefined;
  const article = getArticle(firstChange?.title);

  const operationName = (() => {
    switch (activity.actionStr) {
      case "INSERT":
        return "has created the";
      case "UPDATE":
        return `has ${hasUpdatedValue ? "updated" : "set"} the`;
      case "DELETE":
      case "SOFT_DELETE":
        return "has deleted the";
      case "SUB_INSERT":
        return "has added a";
      case "SUB_UPDATE":
        return `has ${hasUpdatedValue ? "updated" : "set"} ${article}`;
      case "SUB_DELETE":
      case "SUB_SOFT_DELETE":
        return "has deleted a";
      case "ASSOCIATE":
        return `has associated this ${formatTableName(
          activity.tableName,
          activity.actionStr
        )} with ${article}`;
      case "DISASSOCIATE":
        return `has disassociated this ${formatTableName(
          activity.tableName,
          activity.actionStr
        )} with ${article}`;
      default:
        return activity.actionStr;
    }
  })();

  const operationEntity = (() => {
    switch (activity.actionStr) {
      case "UPDATE":
        return "";
      case "INSERT":
      case "DELETE":
      case "SOFT_DELETE":
        return activity.customName ?? formatTableName(activity.tableName, activity.actionStr);
      case "SUB_INSERT":
      case "SUB_UPDATE":
      case "ASSOCIATE":
      case "DISASSOCIATE":
      case "SUB_SOFT_DELETE": {
        const tableName = formatTableName(activity.subTable, activity.actionStr);
        const display = activity.subTableDisplay ? ` (${activity.subTableDisplay})` : "";
        return `${tableName}${display}`;
      }
      case "SUB_DELETE":
        return formatTableName(activity.subTable, activity.actionStr);
      default:
        return "";
    }
  })();

  return [operationName, operationEntity].filter(Boolean).join(" ");
}

function getCategory(change: PatientRecentActivityChange) {
  return change.category?.trim() || "General Details";
}

function getUserName(activity: PatientRecentActivityRow) {
  switch (activity.updatedByUserType) {
    case "CAREGIVER":
    case "AGENCY":
      return activity.updatedByUser ?? "The system";
    case "MEDFLYT":
      return "Medflyt team";
    case "OTHER_AGENCY":
    default:
      return "The system";
  }
}

function getActorPhotoUrl(
  activity: PatientRecentActivityRow,
  agencyMembersByUserId: ReturnType<
    typeof usePatientRecentActivityActorPhotos
  >["agencyMembersByUserId"]
) {
  if (
    activity.updatedByUserType !== "AGENCY" ||
    activity.updatedBy === null ||
    activity.updatedBy === undefined
  ) {
    return undefined;
  }

  return agencyMembersByUserId.get(String(activity.updatedBy))?.photoUrl ?? undefined;
}

function formatActivityValue(change: PatientRecentActivityChange, value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "None";
  }

  if (change.fieldType === "boolean") {
    return value === true ? "Yes" : value === false ? "No" : "None";
  }

  if (
    change.fieldType === "date" ||
    change.fieldType === "timestamp" ||
    change.fieldType === "timestamp with time zone" ||
    change.fieldType === "timestamp without time zone"
  ) {
    return formatActivityDate(value) || "None";
  }

  if (change.fieldType === "jsonb") {
    return Array.isArray(value) ? (value.length > 0 ? value.join(", ") : "None") : "changed";
  }

  return formatPatientProfileValue(value) || "None";
}

function getChangePrefix(change: PatientRecentActivityChange, oldOrNew: "old" | "new") {
  const hasOld = change.old !== undefined && change.old !== null;
  const hasNew = change.new !== undefined && change.new !== null;

  if (hasOld && hasNew) {
    return oldOrNew === "old" ? " from " : " to ";
  }

  if (hasOld && !hasNew) {
    return oldOrNew === "old" ? ": " : "";
  }

  return oldOrNew === "new" ? ": " : "";
}

function ActivityChangeText(props: {
  change: PatientRecentActivityChange;
  index?: number;
  isInline?: boolean;
  showNumber?: boolean;
}) {
  const { change } = props;
  const showOld = change.old !== undefined && change.old !== null;
  const showNew = change.new !== undefined && change.new !== null;

  return (
    <Text {...getActivityChangeTextProps(props.isInline)}>
      {props.showNumber ? `${(props.index ?? 0) + 1}. ` : null}
      <Box {...activityChangeFieldProps}>{change.title}</Box>
      {showOld ? (
        <>
          {getChangePrefix(change, "old")}
          <Box as="strong">{formatActivityValue(change, change.old)}</Box>
        </>
      ) : null}
      {showNew ? (
        <>
          {getChangePrefix(change, "new")}
          <Box as="strong">{formatActivityValue(change, change.new)}</Box>
        </>
      ) : null}
      {showOld || showNew ? null : (
        <>
          : <Box as="strong">None</Box>
        </>
      )}
    </Text>
  );
}

export default function PatientProfileRecentActivitySection(props: Props) {
  const activityQuery = usePatientRecentActivity(props.patientId);
  const actorPhotos = usePatientRecentActivityActorPhotos();
  const [selectedCategories, setSelectedCategories] = React.useState<string[]>([]);
  const authState = auth.getAuthState();
  const isOnboardingAgency =
    authState.type === "authenticated" && authState.data.agency.id === env.MEDFLYT_AGENCY_ID;
  const activities = React.useMemo(
    () =>
      activityQuery.data?.list
        .map((activity) => ({
          ...activity,
          changes: activity.changes
            .filter((change) => change.old !== change.new)
            .map((change) => ({ ...change, category: getCategory(change) })),
        }))
        .filter((activity) => activity.changes.length > 0) ?? [],
    [activityQuery.data?.list]
  );
  const categories = React.useMemo(
    () =>
      Array.from(
        new Set(
          activities.flatMap((activity) => activity.changes.map((change) => getCategory(change)))
        )
      ).sort((a, b) => a.localeCompare(b)),
    [activities]
  );
  const filteredActivities = React.useMemo(
    () =>
      selectedCategories.length === 0
        ? activities
        : activities
            .map((activity) => ({
              ...activity,
              changes: activity.changes.filter((change) =>
                selectedCategories.includes(getCategory(change))
              ),
            }))
            .filter((activity) => activity.changes.length > 0),
    [activities, selectedCategories]
  );
  const timelineItems = React.useMemo<EntityProfileRecentActivityTimelineItem[]>(
    () =>
      filteredActivities.map((activity) => {
        const userName = getUserName(activity);
        const isMultiChange = activity.changes.length > 1;

        return {
          actorName: userName,
          actorPhotoUrl: getActorPhotoUrl(activity, actorPhotos.agencyMembersByUserId),
          category: getCategory(activity.changes[0]),
          content: (
            <>
              <Text {...activityOperationTextProps}>{getOperation(activity)}</Text>
              {isMultiChange ? null : " "}
              {isMultiChange ? (
                <VStack {...activityMultiChangeListProps}>
                  {activity.changes.map((change, index) => (
                    <ActivityChangeText
                      key={`${activity.id}-${change.title}-${index}`}
                      showNumber
                      change={change}
                      index={index}
                    />
                  ))}
                </VStack>
              ) : (
                activity.changes.map((change, index) => (
                  <ActivityChangeText
                    key={`${activity.id}-${change.title}-${index}`}
                    isInline
                    change={change}
                  />
                ))
              )}
            </>
          ),
          date: formatActivityDate(activity.updated ?? activity.createdAt ?? activity.updatedAt),
          id: activity.id,
        };
      }),
    [actorPhotos.agencyMembersByUserId, filteredActivities]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load recent activity."
      isError={activityQuery.isError}
      isLoading={activityQuery.isLoading}
      loadingLabel="Loading recent activity"
      onRetry={() => void activityQuery.refetch()}
    >
      <VStack {...activityRootProps}>
        {isOnboardingAgency ? (
          <VStack {...activityOnboardingProps}>
            <HStack justifyContent="flex-start">
              <EntityModalButton
                kind="blue"
                size="sm"
                onClick={() => props.actions.openTaskActivity({ patientId: props.patientId })}
              >
                Open Task Activity
              </EntityModalButton>
            </HStack>
            <PatientTaskChat patientId={props.patientId} />
          </VStack>
        ) : null}

        {activities.length === 0 ? (
          <EntityProfileEmptyState>No recent activity.</EntityProfileEmptyState>
        ) : (
          <EntityProfileRecentActivityTimeline
            categories={categories}
            items={timelineItems}
            selectedCategories={selectedCategories}
            onSelectedCategoriesChange={setSelectedCategories}
          />
        )}
      </VStack>
    </EntityProfileQuerySection>
  );
}
